import { useIntl } from 'react-intl';
import { Modal, Form } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';

import { NUMBER_TEXT } from 'constants/form';
import { convertXlsxToJSON } from 'utils/upload';
import { useCancellablePromise } from 'utils/promise';
import {
  AMAZON_TIPS_HEADER_MAPPING,
  AMAZON_TIPS_DATE_FIELDS,
  AMAZON_TIPS_SHEET
} from 'constants/cod-reports';
import { DATE_FORMAT } from 'constants/date-picker';
import { validateAmazonTipSheet } from 'utils/cod-reports';
import { excelSerialDateToDate } from 'utils/helpers';
import { FILE_SHEET_UPLOAD_ERROR_TYPES } from 'constants/upload';
import { openModal } from 'utils/modal';
import { uploadAmazonTips } from 'services/cod-reports';

import LoadingWrapper from 'components/LoadingWrapper/LoadingWrapper';
import AmazonTipsErrorsModal from 'containers/CodTransactions/UploadTransactions/components/AmazonTipsErrorsModal/AmazonTipsErrorsModal';
import BRButton from 'components/BRButton/BRButton';
import BRFormInputs from 'components/BRFormInputs/BRFormInputs';
import UploadAttachment from 'components/SupportTickets/components/UploadAttachment/UploadAttachment';
import { notify } from 'components/Notify/Notify';

import './UploadAmazonInvoiceModal.less';

const UploadAmazonInvoiceModal = ({ close, ...props }) => {
  const [isUploading, setIsUploading] = useState(false);

  const [form] = Form.useForm();
  const intl = useIntl();

  const { cancellablePromise } = useCancellablePromise();

  const formRef = {
    current: form
  };

  const formatAmazonTipsDateFields = (delivery) => {
    Object.keys(delivery).map((key) => {
      const isDateField = AMAZON_TIPS_DATE_FIELDS.includes(key);

      if (isDateField && delivery[key]) {
        const date = dayjs(excelSerialDateToDate(delivery[key]));

        delivery[key] = date.isValid() ? dayjs(date).format(DATE_FORMAT) : '';
      }
    });

    return delivery;
  };

  const convertValueToString = (obj) => {
    Object.keys(obj).forEach((k) => {
      obj[k] = obj[k]?.toString();
    });

    return obj;
  };

  const formatAmazonTipsDeliveries = (deliveries) => {
    return deliveries.map((delivery) => {
      const formattedDatesDelivery = formatAmazonTipsDateFields(delivery);
      const { creditInvoiceDate, termsNetDueDate, invoiceDate, ...rest } =
        formattedDatesDelivery;

      const formattedDelivery = {
        creditInvoiceDate,
        termsNetDueDate,
        invoiceDate,
        ...convertValueToString(rest)
      };

      const {
        trackingNumber,
        valueAddedTaxAmount,
        valueAddedTaxPercent,
        TaxType,
        PackageTransportationCost,
        ExtraCost,
        NetPackageTransportationCost,
        ExtraCostDefinition
      } = formattedDelivery;

      return {
        trackingNumber,
        valueAddedTaxAmount,
        valueAddedTaxPercent,
        TaxType,
        PackageTransportationCost,
        ExtraCost,
        NetPackageTransportationCost,
        ExtraCostDefinition
      };
    });
  };

  const formatAmazonTipsPayload = (deliveries) => {
    const {
      invoiceNumber,
      invoiceDate,
      currencyCode,
      accountNumber,
      billToName,
      billToCompany,
      billToAddress1,
      billToAddress2,
      billToCity,
      remitToName,
      remitToAddress,
      remitToCityName,
      remitToStateOrProvinceCode,
      remitToPostalCode,
      remitToCountryCode,
      termsNetDueDate,
      termsNetDays,
      termsDiscountAmount,
      AmazonVATId,
      CarrierVATId
    } = deliveries[0];

    return {
      invoiceNumber,
      invoiceDate: invoiceDate.toString(),
      currencyCode,
      accountNumber,
      billToName,
      billToCompany,
      billToAddress1,
      billToAddress2,
      billToCity,
      remitToName,
      remitToAddress,
      remitToCityName,
      remitToStateOrProvinceCode,
      remitToPostalCode,
      remitToCountryCode,
      termsNetDueDate: termsNetDueDate.toString(),
      termsNetDays,
      termsDiscountAmount,
      AmazonVATId,
      CarrierVATId,
      deliveries: formatAmazonTipsDeliveries(deliveries)
    };
  };

  const OnUploadAmazonInvoiceReportTemplate = async (file) => {
    try {
      setIsUploading(true);

      const data = await convertXlsxToJSON(file, {
        headerMapping: AMAZON_TIPS_HEADER_MAPPING,
        sheetType: AMAZON_TIPS_SHEET
      });

      const { isValid, errors } = await cancellablePromise(
        validateAmazonTipSheet(data)
      );

      if (isValid) {
        const payload = formatAmazonTipsPayload(data);

        form.setFieldsValue(payload);
      } else {
        openModal(AmazonTipsErrorsModal, {
          errors,
          formatMessage: intl.formatMessage
        });
      }
    } catch (error) {
      if (FILE_SHEET_UPLOAD_ERROR_TYPES[error.errorType]) {
        return notify(
          intl.formatMessage({
            id: `common.${FILE_SHEET_UPLOAD_ERROR_TYPES[
              error.errorType
            ].toLowerCase()}`
          })
        );
      }
      notify(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleOnUpload = async (values) => {
    const payload = {
      ...values
    };

    delete payload.uploadFile;

    try {
      setIsUploading(true);

      await uploadAmazonTips(payload);
      notify(
        intl.formatMessage({
          id: 'bulk_update_pricing_tier.success_state.title'
        }),
        'success'
      );
      close();
    } catch (error) {
      notify(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal
      onCancel={close}
      wrapClassName="br-upload-amazon-invoice-report"
      title={intl.formatMessage({
        id: 'cod_reports.amazon.upload_invoice_report'
      })}
      footer={
        <>
          <BRButton
            className="button-md"
            onClick={close}
            label={intl.formatMessage({
              id: 'common.cancel'
            })}
          />

          <BRButton
            className="button-md"
            type="primary"
            htmlType="submit"
            form="upload-amazon-invoice-report"
            label={intl.formatMessage({
              id: 'common.upload'
            })}
          />
        </>
      }
      {...props}
    >
      <LoadingWrapper loading={isUploading}>
        <Form
          form={form}
          onFinish={handleOnUpload}
          name="upload-amazon-invoice-report"
        >
          <BRFormInputs
            type={NUMBER_TEXT}
            formRef={formRef}
            name="totalValueAddedTaxAmount"
            inputTitle={intl.formatMessage({
              id: 'cod_reports.amazon.total_value_added_tax_amount'
            })}
            acceptFractions
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'cod_reports.amazon.total_value_added_tax_amount_required'
                })
              }
            ]}
          />
          <BRFormInputs
            type={NUMBER_TEXT}
            formRef={formRef}
            name="totalPackageTransportationCost"
            inputTitle={intl.formatMessage({
              id: 'cod_reports.amazon.total_package_transportation_cost'
            })}
            acceptFractions
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'cod_reports.amazon.total_package_transportation_cost_required'
                })
              }
            ]}
          />
          <BRFormInputs
            type={NUMBER_TEXT}
            formRef={formRef}
            name="totalNetPackageTransportationCost"
            inputTitle={intl.formatMessage({
              id: 'cod_reports.amazon.total_net_package_transportation_cost'
            })}
            acceptFractions
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'cod_reports.amazon.total_net_package_transportation_cost_required'
                })
              }
            ]}
          />

          <Form.Item
            name="uploadFile"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'cod_reports.amazon.deliveries_required'
                })
              }
            ]}
          >
            <UploadAttachment
              className="br-upload-amazon-invoice-report__upload-attachment"
              accept=".xlsx"
              hideUploadButtonAfterMaxCount
              maxCount={1}
              beforeUpload={OnUploadAmazonInvoiceReportTemplate}
            />
          </Form.Item>

          <Form.Item name="invoiceNumber" hidden />
          <Form.Item name="invoiceDate" hidden />
          <Form.Item name="currencyCode" hidden />
          <Form.Item name="accountNumber" hidden />
          <Form.Item name="billToName" hidden />
          <Form.Item name="billToCompany" hidden />
          <Form.Item name="billToAddress1" hidden />
          <Form.Item name="billToAddress2" hidden />
          <Form.Item name="billToCity" hidden />
          <Form.Item name="remitToName" hidden />
          <Form.Item name="remitToAddress" hidden />
          <Form.Item name="remitToCityName" hidden />
          <Form.Item name="remitToStateOrProvinceCode" hidden />
          <Form.Item name="remitToPostalCode" hidden />
          <Form.Item name="remitToCountryCode" hidden />
          <Form.Item name="termsNetDueDate" hidden />
          <Form.Item name="termsNetDays" hidden />
          <Form.Item name="termsDiscountAmount" hidden />
          <Form.Item name="AmazonVATId" hidden />
          <Form.Item name="CarrierVATId" hidden />
          <Form.Item name="deliveries" hidden />
        </Form>
      </LoadingWrapper>
    </Modal>
  );
};

export default UploadAmazonInvoiceModal;
